import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../../Config/axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./CodeMatrixDeleteDialog.css";

export default function CodeMatrixDeleteDialog({
  open,
  onClose,
  row,
  onDeleted,
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const rawRow = useMemo(() => row ?? null, [row]);

  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  const handleDelete = async () => {
    if (!rawRow?.id) return;

    setError("");
    setDeleting(true);

    try {
      await axiosInstance.delete(`/code_matrix/${rawRow.id}/`);
      onDeleted?.();
    } catch (e) {
      setError(
        "Cannot delete this code matrix because it is used in other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="matrix-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {!error && (
        <DialogTitle className="mdel-header">
          <Box className="mdel-header-content">
            <Box className="mdel-title-section">
              <WarningAmberIcon className="mdel-warning-icon" />
              <Typography variant="h5" component="span" className="mdel-title">
                Delete Code Matrix
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              className="mdel-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="mdel-content">
        {error ? (
          <Alert
            severity="error"
            className="mdel-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Code Matrix
            </Typography>
            <Typography variant="body2" className="mdel-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          <Box className="mdel-confirm-content">
            {(rawRow?.department || rawRow?.activity) && (
              <Box className="mdel-code-container">
                <Typography variant="subtitle2" className="mdel-code-label">
                  Code Matrix
                </Typography>

                <Typography variant="h6" className="mdel-code-value">
                  {rawRow?.department ?? "-"} - {rawRow?.activity ?? "-"}
                </Typography>
              </Box>
            )}

            <Typography variant="body1" className="mdel-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="mdel-consequences">
              <Typography variant="body2" className="mdel-consequence-item">
                • The selected code matrix record will be permanently deleted
              </Typography>
              <Typography variant="body2" className="mdel-consequence-item">
                • Related configurations may be affected
              </Typography>
              <Typography variant="body2" className="mdel-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="mdel-actions">
        {error ? (
          <Button
            onClick={onClose}
            variant="contained"
            className="mdel-btn mdel-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="mdel-btn mdel-btn-cancel"
            >
              Cancel
            </Button>

            <Button
              onClick={handleDelete}
              disabled={deleting || !rawRow?.id}
              variant="contained"
              className="mdel-btn mdel-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}